import { isValidElement } from 'react'

import moment from 'moment-timezone'
import { Column, GenericRow } from './types'
import { isPromise } from 'lib/utils'

type ROW_TYPE = 'data' | 'summary'

const RENDERERS = {
  date: value => value._isAMomentObject ? value.format('M/D/Y') : '',
  numeric: value => typeof(value) === 'number' ?  value.toLocaleString() : '',
}

const DEFAULT_RENDERER = value => value

export function rawCell<RowType extends GenericRow, ValueType>(row: RowType, column: Column<RowType, ValueType>): ValueType | undefined {
  if(column.value) {
    return column.value(row)
  }

  if(column.field === undefined) {
    return undefined
  }

  const value = row[column.field]

  if('type' in column && (column.type === 'date' || column.type === 'datetime')) {
    return moment(value) as ValueType // TODO: should be able to infer that ValueType extends Moment
  }
  if('lookup' in column && !isPromise(column.lookup)) {
    return column.lookup[value]
  }

  return value
}

export function renderCell<RowType extends GenericRow, ValueType>(row: RowType, column: Column<RowType, ValueType>, renderContext = {}) {
  return renderValue(rawCell(row, column), row, column, 'data', renderContext)
}

export function getRendererFor<RowType extends GenericRow, ValueType>(column: Column<RowType, ValueType>, rowType: ROW_TYPE) {
  return (rowType === 'summary' && column.renderSummary) || column.render || (column.type && RENDERERS[column.type]) || DEFAULT_RENDERER
}


function renderValue(value, row, column, rowType: ROW_TYPE = 'data', renderContext = {}) {
  const rendered = getRendererFor(column, rowType)(value, row, renderContext)

  if(rendered === null || rendered === undefined) {
    return ''
  }

  if(isValidElement(rendered)) {
    return rendered
  }

  if(typeof(rendered) === 'number') {
    return rendered.toLocaleString()
  }

  return `${rendered}`
}

function rawSummary(data, column) {
  if(column.summary === 'sum') {
    return data.map(row => rawCell(row, column)).reduce((a,b) => a + b, 0)
  } else if(typeof(column.summary) === 'function') {
    return column.summary(data)
  }
  return ''
}

export function renderSummary(data, column, renderContext) {
  if(typeof(column.summary) === 'object') {
    return `${column.summary.text}`
  }

  return renderValue(rawSummary(data, column), {}, column, 'summary', renderContext)
}

function stripSymbols(value) {
  if(typeof(value) === 'string') {
    return value.replace(/[%$,]/g, '');
  } else {
    return value
  }
}

export function compareCells(value1, value2) {
  value1 = stripSymbols(value1)
  value2 = stripSymbols(value2)

  if(value1 === null || value1 === undefined) {
    if(value2 === null || value2 === undefined) {
      return 0
    } else {
      return -1
    }
  }

  if(value2 === null || value2 === undefined) {
    return 1
  }

  if(isFinite(value1) && isFinite(value2)) {
    return value1 - value2
  }
  else {
    return `${value1}`.localeCompare(`${value2}`)
  }
}
